import { useEffect, useMemo, useState } from "react";
import api, { fmtSeconds, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, Receipt } from "lucide-react";
import { toast } from "sonner";

// Build month options for the past 12 months + next 1
function buildMonths() {
  const months = [];
  const now = new Date();
  for (let i = -12; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString(undefined, { month: "long", year: "numeric" }),
    });
  }
  return months.reverse(); // most recent first
}

function lastDayOfMonth(year, monthIdx0) {
  return new Date(year, monthIdx0 + 1, 0).getDate();
}

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

// Quick preset chips for common pay periods
function presets() {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const lastOfMonth = lastDayOfMonth(y, m);
  const firstOfMonth = ymd(new Date(y, m, 1));
  const fifteenth = ymd(new Date(y, m, 15));
  const sixteenth = ymd(new Date(y, m, 16));
  const endOfMonth = ymd(new Date(y, m, lastOfMonth));
  // Previous month
  const prev = new Date(y, m - 1, 1);
  const prevY = prev.getFullYear();
  const prevM = prev.getMonth();
  const prevLast = lastDayOfMonth(prevY, prevM);
  const prevFirst = ymd(new Date(prevY, prevM, 1));
  const prevEnd = ymd(new Date(prevY, prevM, prevLast));
  const prev15 = ymd(new Date(prevY, prevM, 15));
  const prev16 = ymd(new Date(prevY, prevM, 16));
  // Rolling windows
  const last7 = new Date(); last7.setDate(today.getDate() - 6);
  const last30 = new Date(); last30.setDate(today.getDate() - 29);
  return [
    { id: "this_1_15",  label: "This Month · 1–15",   start: firstOfMonth, end: fifteenth },
    { id: "this_16_end",label: "This Month · 16–End", start: sixteenth,    end: endOfMonth },
    { id: "this_full",  label: "This Month · Full",   start: firstOfMonth, end: endOfMonth },
    { id: "prev_1_15",  label: "Last Month · 1–15",   start: prevFirst,    end: prev15 },
    { id: "prev_16_end",label: "Last Month · 16–End", start: prev16,       end: prevEnd },
    { id: "prev_full",  label: "Last Month · Full",   start: prevFirst,    end: prevEnd },
    { id: "last7",      label: "Last 7 days",         start: ymd(last7),   end: ymd(today) },
    { id: "last30",     label: "Last 30 days",        start: ymd(last30),  end: ymd(today) },
  ];
}

export default function InvoicesPage({ scope = "admin" }) {
  const isClient = scope === "client";
  const today = ymd(new Date());
  // Default range: this month, 1st → today
  const initToday = new Date();
  const firstThis = ymd(new Date(initToday.getFullYear(), initToday.getMonth(), 1));
  const [startDate, setStartDate] = useState(firstThis);
  const [endDate, setEndDate] = useState(today);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const PRESETS = useMemo(presets, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fetch clients list (admin scope only)
      try {
        if (!isClient) {
          const { data: list } = await api.get("/admin/clients");
          if (!cancelled) setClients(list);
        }
      } catch (_) { /* ignore */ }
      // Auto-generate the default invoice once on mount
      try {
        const params = { start_date: startDate, end_date: endDate };
        if (clientId !== "all") params.client_id = clientId;
        const { data: d } = await api.get("/invoices/generate", { params });
        if (!cancelled) setData(d);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Match the currently-selected dates to a preset id (for highlighting the active chip)
  const activePresetId = PRESETS.find((p) => p.start === startDate && p.end === endDate)?.id;

  const applyPreset = (p) => { setStartDate(p.start); setEndDate(p.end); };

  const monthLabel = (() => {
    if (!startDate || !endDate) return "";
    const s = new Date(startDate);
    return `${s.toLocaleString(undefined, { month: "long", year: "numeric" })}`;
  })();

  const generate = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select a valid date range");
      return;
    }
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (clientId !== "all") params.client_id = clientId;
      const { data } = await api.get("/invoices/generate", { params });
      setData(data);
      toast.success(`Generated invoice: ${data.daily_rows.length} day-row(s) across ${data.active_employees} employee(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data || !data.daily_rows.length) return;
    const lines = [
      ["Date", "Employee", "Email", "Business", "Productive (h)", "Idle (h)", "Adjustment (m)", "Rate", "Currency", "Billable"].join(","),
      ...data.daily_rows.map((r) => [
        r.date,
        (r.name || "").replace(/,/g, ""),
        r.email || "",
        (r.business_name || "").replace(/,/g, ""),
        (r.productive_seconds / 3600).toFixed(2),
        (r.idle_seconds / 3600).toFixed(2),
        (r.adjustment_seconds / 60).toFixed(0),
        r.hourly_rate.toFixed(2),
        r.currency,
        r.billable_amount.toFixed(2),
      ].join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `invoice-${data.start_date}_to_${data.end_date}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!data) return;
    // Build date range (use ymd() for local timezone — toISOString shifts to UTC and would miss the boundary day)
    const allDates = [];
    if (data.start_date && data.end_date) {
      const s = new Date(data.start_date + "T00:00:00");
      const e = new Date(data.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        allDates.push(ymd(d));
      }
    }
    const employeeCols = [...(data.rows || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const grid = {};
    (data.daily_rows || []).forEach((r) => {
      grid[r.date] = grid[r.date] || {};
      grid[r.date][r.user_id] = r.productive_seconds;
    });
    const fmtHrs = (sec) => {
      if (!sec || sec <= 0) return "";
      const total = Math.round(sec);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    const w = window.open("", "_blank");
    if (!w) return;

    const headEmployees = employeeCols.map((e) =>
      `<th>${e.name || ""}<div class="biz">${e.business_name && e.business_name !== "—" ? e.business_name : ""}</div></th>`
    ).join("");

    const dateRows = allDates.map((d) => {
      const dDate = new Date(d + "T00:00:00");
      const label = dDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      const cells = employeeCols.map((e) => {
        const sec = grid[d]?.[e.user_id] || 0;
        return `<td class="num">${fmtHrs(sec)}</td>`;
      }).join("");
      return `<tr><td class="dt">${label}</td>${cells}</tr>`;
    }).join("");

    const totalsRow = `<tr class="totals"><td>Total</td>${employeeCols.map((e) => {
      let sec = 0;
      allDates.forEach((d) => { sec += (grid[d]?.[e.user_id] || 0); });
      return `<td class="num">${sec > 0 ? fmtHrs(sec) : ""}</td>`;
    }).join("")}</tr>`;

    const billableRow = `<tr class="billable"><td>Billable</td>${employeeCols.map((e) =>
      `<td class="num">${e.billable_amount > 0 ? e.currency + " " + e.billable_amount.toFixed(2) : "—"}</td>`
    ).join("")}</tr>`;

    const totalsHtml = Object.entries(data.totals_by_currency).map(([cur, amt]) =>
      `<div><div class="lbl">${cur}</div><div class="val">${cur} ${amt.toFixed(2)}</div></div>`
    ).join("");

    w.document.write(`<!doctype html><html><head><title>Invoice — ${data.start_date} to ${data.end_date}</title>
      <style>
        body{font-family:'Inter',sans-serif;padding:32px;color:#1f2e22}
        h1{font-family:'Cormorant Garamond',serif;letter-spacing:-1px;font-weight:500;font-size:32px;margin:0}
        .gold{color:#d4a82a;font-style:italic}
        .period{color:#666;font-size:13px;margin-top:4px}
        table{width:100%;border-collapse:collapse;margin-top:24px;font-size:11px;table-layout:auto}
        th,td{border:1px solid #999;padding:8px;text-align:center}
        th{background:#f5f5f4;font-weight:bold}
        th.section{background:#f5f5f4;color:#1f4d2e;text-transform:uppercase;letter-spacing:2px;font-size:10px}
        .banner{background:#1f4d2e;color:#fff;font-style:italic;padding:6px}
        .biz{font-size:9px;color:#666;font-weight:normal;letter-spacing:1px;text-transform:uppercase;margin-top:2px}
        td.dt{text-align:left;font-family:'IBM Plex Mono',monospace}
        td.num{font-family:'IBM Plex Mono',monospace;font-weight:600;color:#1f4d2e}
        tr.totals td{background:#fdf8e8;font-weight:bold;color:#7a5e0f;text-transform:uppercase;letter-spacing:1px}
        tr.billable td{background:#f0f5f1;font-weight:bold;color:#1f4d2e;text-transform:uppercase;letter-spacing:1px}
        .summary{display:flex;gap:24px;margin-top:16px;padding:14px;border:1px solid #d4a82a;background:#fdf8e8}
        .summary div{flex:1}
        .lbl{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#777}
        .val{font-size:22px;font-weight:bold;color:#1f4d2e;font-family:'IBM Plex Mono',monospace}
        .brand{display:flex;align-items:center;gap:12px;margin-bottom:24px}
        .brand img{height:48px;width:48px;border-radius:6px}
      </style></head><body>
      <div class="brand">
        <img src="/taskmaster-logo.jpg" alt="" onerror="this.style.display='none'" />
        <div>
          <h1>Taskmaster <span class="gold">Philippines</span></h1>
          <div style="font-style:italic;color:#666;font-size:13px">Your Virtual Assistance, Your Virtual services</div>
        </div>
      </div>
      <h2 style="font-family:'Cormorant Garamond',serif;font-weight:500;font-size:26px;margin:0">Time Rendered</h2>
      <div class="period">Period: ${data.start_date} → ${data.end_date}</div>
      <div class="summary">${totalsHtml || '<div class="lbl">No billable amounts in this period</div>'}</div>
      <table>
        <thead>
          <tr><th rowspan="2" style="text-align:left">Date</th><th colspan="${employeeCols.length}" class="section">Time Rendered</th></tr>
          <tr>${headEmployees}</tr>
          <tr><td></td><td colspan="${employeeCols.length}" class="banner">Time Rendered</td></tr>
        </thead>
        <tbody>${dateRows}${totalsRow}${billableRow}</tbody>
      </table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ {isClient ? "Client" : "Super Admin"} / Invoices ]</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {isClient ? "Billable Hours & Invoices" : "Invoice Generator"}
            </h1>
            <p className="font-display-italic text-sm text-stone-500 mt-1">
              {isClient
                ? "Productive hours and billable totals for your assigned team"
                : "Productive hours × hourly rate, by pay period"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="invoice-export-csv" onClick={exportCSV} variant="outline" disabled={!data} className="rounded-md border-stone-300">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button data-testid="invoice-export-pdf" onClick={exportPDF} disabled={!data} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90 text-white">
              <Download className="h-4 w-4 mr-2" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Filters — pick a precise date range with two date pickers and quick presets */}
        <div className="bg-white border border-stone-200 rounded-md p-4 mb-6 space-y-4">
          {/* Quick preset chips */}
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600 mb-2 block">Quick periods</Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`invoice-preset-${p.id}`}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase font-bold rounded-md border transition-colors ${
                    activePresetId === p.id
                      ? "bg-[#1f4d2e] text-white border-[#1f4d2e]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-[#1f4d2e] hover:text-[#1f4d2e]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-3 items-end ${isClient ? "md:grid-cols-[1fr_1fr_auto]" : "md:grid-cols-[1fr_1fr_1fr_auto]"}`}>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Start date</Label>
              <Input
                data-testid="invoice-start-date"
                type="date"
                max={endDate || today}
                className="rounded-md mt-1.5 font-mono-num"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">End date</Label>
              <Input
                data-testid="invoice-end-date"
                type="date"
                min={startDate}
                max={today}
                className="rounded-md mt-1.5 font-mono-num"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {!isClient && (
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Business</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="invoice-client-select" className="rounded-md mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-md max-h-72">
                    <SelectItem value="all">All businesses</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="generate-invoice-button" onClick={generate} disabled={loading} className="rounded-md bg-[#d4a82a] hover:bg-[#b8902a] text-[#1f4d2e] font-semibold h-10">
              <Receipt className="h-4 w-4 mr-2" />
              {loading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>

        {/* Header info */}
        {data && (
          <div className="mb-6">
            <div className="inline-flex items-center gap-3 mb-3">
              <span className="gold-rule" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#7a5e0f] font-bold">Pay Period</span>
              <span className="gold-rule" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-[#1f4d2e]">
              {monthLabel} <span className="font-display-italic text-[#d4a82a]">· {data.start_date} → {data.end_date}</span>
            </h2>
            <div className="font-mono-num text-sm text-stone-500 mt-1">
              {data.start_date} → {data.end_date}
              <span className="mx-2">·</span>
              {data.active_employees} of {data.total_employees} employees active
            </div>
          </div>
        )}

        {/* Totals */}
        {data && Object.keys(data.totals_by_currency).length > 0 && (
          <div className="mb-6 p-6 rounded-md border border-[#d4a82a]/40 bg-[#d4a82a]/10 flex items-center justify-between flex-wrap gap-6">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#7a5e0f] font-bold">Total billable</div>
            <div className="flex gap-10 flex-wrap">
              {Object.entries(data.totals_by_currency).map(([cur, amt]) => (
                <div key={cur}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">{cur}</div>
                  <div className="font-mono-num text-3xl font-bold text-[#1f4d2e]">{fmtMoney(amt, cur)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matrix view: Date × Employees */}
        {(() => {
          if (!data) {
            return (
              <div className="border border-stone-200 rounded-md bg-white p-12 text-center text-sm text-stone-500">Generating invoice…</div>
            );
          }
          // Build the date range (every calendar day in the period)
          const allDates = [];
          if (data.start_date && data.end_date) {
            const s = new Date(data.start_date + "T00:00:00");
            const e = new Date(data.end_date + "T00:00:00");
            for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
              allDates.push(ymd(d));
            }
          }
          // Columns = employees that are shown in this report; sort by name
          const employeeCols = [...(data.rows || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          // Lookup: { [date]: { [user_id]: productive_seconds } }
          const grid = {};
          (data.daily_rows || []).forEach((r) => {
            grid[r.date] = grid[r.date] || {};
            grid[r.date][r.user_id] = r.productive_seconds;
          });

          if (employeeCols.length === 0) {
            return (
              <div className="border border-stone-200 rounded-md bg-white p-12 text-center text-sm text-stone-500">
                No employees match this filter.
              </div>
            );
          }

          // Column totals (for footer row)
          const colTotals = {};
          employeeCols.forEach((e) => { colTotals[e.user_id] = 0; });
          allDates.forEach((d) => {
            employeeCols.forEach((e) => {
              colTotals[e.user_id] += (grid[d]?.[e.user_id] || 0);
            });
          });

          const fmtHrs = (sec) => {
            if (!sec || sec <= 0) return "";
            // Show as H:MM:SS (trim leading zero hour) — readable for both 4:00:00 and 0:02:19
            const total = Math.round(sec);
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = total % 60;
            return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
          };

          return (
            <div className="border border-stone-200 rounded-md overflow-x-auto bg-white">
              <table className="w-full text-sm border-collapse" style={{ minWidth: `${260 + employeeCols.length * 160}px` }}>
                <thead>
                  {/* Row 1 — section labels */}
                  <tr>
                    <th rowSpan={2} className="border border-stone-300 bg-stone-50 px-4 py-3 text-left font-bold tracking-wide text-stone-700 align-middle">
                      Date
                    </th>
                    <th colSpan={employeeCols.length} className="border border-stone-300 bg-stone-50 px-4 py-3 text-center font-bold tracking-[0.2em] uppercase text-[#1f4d2e] text-[11px]">
                      Time Rendered
                    </th>
                  </tr>
                  {/* Row 2 — Full Name header per employee */}
                  <tr>
                    {employeeCols.map((e) => (
                      <th key={e.user_id} className="border border-stone-300 bg-white px-3 py-3 text-center font-semibold text-stone-800 align-middle">
                        <div className="font-display text-[15px] leading-tight">{e.name}</div>
                        {e.business_name && e.business_name !== "—" && (
                          <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mt-0.5">{e.business_name}</div>
                        )}
                        {(!e.hourly_rate || e.hourly_rate <= 0) && (
                          <div
                            data-testid={`rate-unset-tag-${e.user_id}`}
                            title="Hourly rate not set — Super Admin must set this in Users page for billable amount to compute."
                            className="mt-1 inline-block text-[9px] tracking-[0.16em] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
                          >
                            Rate unset
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                  {/* Row 3 — green banner */}
                  <tr>
                    <td className="border border-stone-300 bg-[#1f4d2e]" />
                    <td colSpan={employeeCols.length} className="border border-stone-300 bg-[#1f4d2e] text-center text-stone-50 font-display-italic text-base py-2">
                      Time Rendered
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {allDates.map((dateStr) => {
                    const d = new Date(dateStr + "T00:00:00");
                    const dayLabel = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <tr key={dateStr} className={isWeekend ? "bg-stone-50/40" : ""}>
                        <td className="border border-stone-300 px-4 py-2.5 font-mono-num text-stone-700">
                          {dayLabel}
                        </td>
                        {employeeCols.map((e) => {
                          const sec = grid[dateStr]?.[e.user_id] || 0;
                          return (
                            <td key={e.user_id} className="border border-stone-300 px-3 py-2.5 text-center font-mono-num">
                              {sec > 0 ? (
                                <span className="text-[#1f4d2e] font-semibold">{fmtHrs(sec)}</span>
                              ) : (
                                <span className="text-stone-300">&nbsp;</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* Footer row: totals per column */}
                  <tr className="bg-[#d4a82a]/10">
                    <td className="border border-stone-300 px-4 py-3 font-bold tracking-[0.15em] uppercase text-[11px] text-[#7a5e0f]">
                      Total
                    </td>
                    {employeeCols.map((e) => {
                      const tot = colTotals[e.user_id];
                      return (
                        <td key={e.user_id} className="border border-stone-300 px-3 py-3 text-center font-mono-num font-bold text-[#1f4d2e]">
                          {tot > 0 ? fmtHrs(tot) : ""}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Footer row: billable per column */}
                  <tr className="bg-[#1f4d2e]/[0.04]">
                    <td className="border border-stone-300 px-4 py-3 font-bold tracking-[0.15em] uppercase text-[11px] text-[#1f4d2e]">
                      Billable
                    </td>
                    {employeeCols.map((e) => {
                      const rateMissing = !e.hourly_rate || e.hourly_rate <= 0;
                      const hasHours = (colTotals[e.user_id] || 0) > 0;
                      return (
                        <td key={e.user_id} className="border border-stone-300 px-3 py-3 text-center font-mono-num font-bold text-[#1f4d2e]">
                          {e.billable_amount > 0 ? (
                            fmtMoney(e.billable_amount, e.currency)
                          ) : rateMissing && hasHours ? (
                            <span
                              data-testid={`billable-rate-unset-${e.user_id}`}
                              title="Hourly rate not set — Super Admin must set this in Users page."
                              className="text-[10px] tracking-[0.16em] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
                            >
                              Set rate
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Per-employee subtotals — quick reference below the daily breakdown */}
        {data && data.rows && data.rows.filter((r) => r.productive_seconds > 0).length > 0 && (
          <div className="mt-8">
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-3">[ Per-Employee Subtotals ]</div>
            <div className="border border-stone-200 rounded-md overflow-x-auto bg-white">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-stone-50 border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Employee</th>
                    <th className="text-left px-4 py-3 font-bold">Business</th>
                    <th className="text-right px-4 py-3 font-bold">Days</th>
                    <th className="text-right px-4 py-3 font-bold">Total Productive</th>
                    <th className="text-right px-4 py-3 font-bold">Rate</th>
                    <th className="text-right px-4 py-3 font-bold">Billable</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.filter((r) => r.productive_seconds > 0 || r.billable_amount > 0).map((r) => (
                    <tr key={r.user_id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-semibold">{r.name}</td>
                      <td className="px-4 py-3 text-stone-700">{r.business_name}</td>
                      <td className="px-4 py-3 text-right font-mono-num">{r.sessions_count}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-[#1f4d2e] font-semibold">{(r.productive_seconds / 3600).toFixed(2)}h</td>
                      <td className="px-4 py-3 text-right font-mono-num">
                        {r.hourly_rate > 0 ? fmtMoney(r.hourly_rate, r.currency) : <span className="text-stone-400 italic text-xs">unset</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num font-bold text-[#1f4d2e]">
                        {r.billable_amount > 0 ? fmtMoney(r.billable_amount, r.currency) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
